import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import type { CartItem as Item } from '../types/cart';
import { createCartService, CartDriver, fastUpdateCartQuantity, validateAndCleanupCartItems } from '../services/cart/cartService';
import { cartKey } from '../services/cart/keys';
import toast from 'react-hot-toast';
import { sanitizeString, sanitizeError } from '../utils/sanitization';
import { isValidDocumentId, validateString, validateNumber } from '../utils/validation';

// Security: Enhanced number sanitization for cart-specific needs
// Note: This is a specific implementation for cart quantity sanitization
function sanitizeNumber(input: any, min: number = 0, max: number = 1000): number | undefined {
  const num = Number(input);
  if (isNaN(num) || !isFinite(num)) return undefined;
  return Math.max(min, Math.min(max, Math.floor(num)));
}

// Security: Enhanced boolean validation for cart-specific needs
function validateBoolean(value: any, fieldName: string): { valid: boolean; message?: string } {
  if (typeof value !== 'boolean') {
    return { valid: false, message: `${fieldName} must be a boolean` };
  }
  
  return { valid: true };
}

// Security: Enhanced rate limiting for cart operations
// Note: This could be consolidated with ContextRateLimiter in MessagingContext if rate limits remain the same
class CartRateLimiter {
  private attempts: Map<string, number[]> = new Map();
  private readonly maxAttempts = 50; // 50 operations per minute
  private readonly windowMs = 60000; // 1 minute
  private readonly maxAttemptsPerHour = 500; // 500 operations per hour
  private readonly hourlyWindowMs = 3600000; // 1 hour

  isAllowed(key: string): { allowed: boolean; message?: string } {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove old attempts outside the window
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    const hourlyAttempts = attempts.filter(time => now - time < this.hourlyWindowMs);
    
    // Check hourly limit first
    if (hourlyAttempts.length >= this.maxAttemptsPerHour) {
      return {
        allowed: false,
        message: 'Too many cart operations in the last hour. Please wait before trying again.'
      };
    }
    
    if (recentAttempts.length >= this.maxAttempts) {
      return {
        allowed: false,
        message: 'Too many cart operations. Please try again later.'
      };
    }
    
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);
    return { allowed: true };
  }

  getRemainingAttempts(key: string): number {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    return Math.max(0, this.maxAttempts - recentAttempts.length);
  }
}

const cartRateLimiter = new CartRateLimiter();

export type CartItem = Item; // backward-compat type alias

interface CartContextType {
  items: Item[];
  loading: boolean;
  totalItems: number;
  totalPrice: number;
  addOrUpdateItem: (item: Omit<Item, 'quantity'>, quantity?: number) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
  setQuantity: (key: string, quantity: number) => Promise<void>;
  fastSetQuantity: (key: string, quantity: number) => Promise<void>;
  toggleSelected: (key: string, selected: boolean) => Promise<void>;
  clearCart: () => Promise<void>;
  cacheProductStock: (productId: string, stock: number) => void;
  // Backward-compat helpers
  addToCart: (item: Omit<Item, 'quantity'>, quantity?: number) => Promise<void>;
  removeFromCart: (key: string) => Promise<void>;
  updateQuantity: (key: string, quantity: number) => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [driver, setDriver] = useState<CartDriver | null>(null);
  const [productStockCache, setProductStockCache] = useState<Map<string, number>>(new Map());

  // Security: Function to cache product stock data with validation
  const cacheProductStock = useCallback((productId: string, stock: number) => {
    // Security: Validate inputs
    if (!isValidDocumentId(productId)) {
      // SECURITY: Removed console.warn to prevent information leakage
      return;
    }
    
    const sanitizedStock = sanitizeNumber(stock, 0, 10000);
    if (sanitizedStock === undefined) {
      // SECURITY: Removed console.warn to prevent information leakage
      return;
    }
    
    setProductStockCache(prev => new Map(prev).set(productId, sanitizedStock));
  }, []);

  // Switch driver on auth change and start listening
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastValidatedItemsRef = useRef<string[]>([]);
  const isValidationInProgressRef = useRef<boolean>(false);
  
  useEffect(() => {
    // Wait for auth to finish loading before creating the driver
    if (authLoading) {
      return;
    }
    
    // Reset validation state when user changes
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
      validationTimeoutRef.current = null;
    }
    lastValidatedItemsRef.current = [];
    isValidationInProgressRef.current = false;
    
    const d = createCartService(currentUser?.uid);
    setDriver(d);
    
    // Only start listening if we have a valid driver
    if (d && currentUser?.uid) {
      const unsub = d.listen(async (list) => {
        // Prevent validation loop: skip validation if we're already validating
        if (isValidationInProgressRef.current) {
          setItems(list);
          setLoading(false);
          return;
        }
        
        setItems(list);
        setLoading(false);
        
        // Validate and clean up invalid/unavailable items
        // Only validate if items changed (to avoid unnecessary checks)
        const currentItemIds = list.map(item => item.id || cartKey({ productId: item.productId, variationId: item.variationId })).sort().join(',');
        const lastItemIds = lastValidatedItemsRef.current.sort().join(',');
        
        if (list.length > 0 && currentItemIds !== lastItemIds) {
          // Debounce validation to avoid running on every rapid change
          if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
          }
          
          validationTimeoutRef.current = setTimeout(async () => {
            // Set flag to prevent validation loop
            if (isValidationInProgressRef.current) {
              return;
            }
            
            isValidationInProgressRef.current = true;
            try {
              const { invalidItemIds, quantityAdjustments } = await validateAndCleanupCartItems(currentUser.uid, list);
              
              // Update UI state to remove invalid items immediately
              if (invalidItemIds.length > 0) {
                setItems((prev) => prev.filter(i => !invalidItemIds.includes(i.id || cartKey({ productId: i.productId, variationId: i.variationId }))));
                toast.error(`${invalidItemIds.length} item(s) removed from cart - no longer available`);
              }
              
              // Show notification for quantity adjustments
              if (quantityAdjustments.length > 0) {
                toast(`${quantityAdjustments.length} item(s) quantity adjusted due to stock availability`, { icon: 'ℹ️' });
              }
              
              // Update last validated items after a short delay to allow Firestore to sync
              setTimeout(() => {
                lastValidatedItemsRef.current = list
                  .filter(item => !invalidItemIds.includes(item.id || cartKey({ productId: item.productId, variationId: item.variationId })))
                  .map(item => item.id || cartKey({ productId: item.productId, variationId: item.variationId }));
                isValidationInProgressRef.current = false;
              }, 500);
            } catch (error) {
              // Silently fail validation - don't disrupt user experience
              // SECURITY: Removed console.warn to prevent information leakage
              isValidationInProgressRef.current = false;
            }
          }, 1000); // Wait 1 second after last change before validating
        } else if (list.length === 0) {
          lastValidatedItemsRef.current = [];
          isValidationInProgressRef.current = false;
        }
      });
      
      return () => {
        if (validationTimeoutRef.current) {
          clearTimeout(validationTimeoutRef.current);
        }
        isValidationInProgressRef.current = false;
        unsub && unsub();
      };
    } else if (d) {
      // Guest user - no validation needed
      const unsub = d.listen(list => { setItems(list); setLoading(false); });
      return () => unsub && unsub();
    }
  }, [currentUser?.uid, authLoading]);

  const addOrUpdateItem = useCallback(async (newItem: Omit<Item, 'quantity'>, quantity: number = 1) => {
    if (!driver) return;
    
    try {
      // Security: Enhanced rate limiting (only for authenticated users)
      if (currentUser?.uid) {
        const rateLimitKey = `add_item_${currentUser.uid}`;
        const rateLimitResult = cartRateLimiter.isAllowed(rateLimitKey);
        if (!rateLimitResult.allowed) {
          toast.error(rateLimitResult.message || 'Too many cart operations. Please try again later.');
          return;
        }
      }
      
      // Security: Enhanced input validation
      const productIdValidation = validateString(newItem.productId, 'Product ID', 1, 100);
      if (!productIdValidation.valid) {
        toast.error(productIdValidation.message || 'Invalid product ID');
        return;
      }

      const variationIdValidation = validateString(newItem.variationId, 'Variation ID', 1, 100);
      if (!variationIdValidation.valid) {
        toast.error(variationIdValidation.message || 'Invalid variation ID');
        return;
      }

      const quantityValidation = validateNumber(quantity, 'Quantity', 1, 100);
      if (!quantityValidation.valid) {
        toast.error(quantityValidation.message || 'Invalid quantity');
        return;
      }
      
      // Security: Validate product ID format
      if (!isValidDocumentId(newItem.productId)) {
        toast.error('Invalid product ID format');
        return;
      }

      // Security: Validate item data structure
      if (!newItem || typeof newItem !== 'object') {
        toast.error('Invalid item data');
        return;
      }

      // Security: Data size limits
      const MAX_CART_ITEMS = 100;
      if (items.length >= MAX_CART_ITEMS) {
        toast.error('Cart is full. Please remove some items before adding new ones.');
        return;
      }
      
      const key = cartKey({ productId: newItem.productId, variationId: newItem.variationId });
      
      // Security: Enhanced item data sanitization
      const sanitizedItem = {
        ...newItem,
        productId: sanitizeString(newItem.productId, 100) || newItem.productId,
        variationId: sanitizeString(newItem.variationId, 100) || newItem.variationId,
        quantity: sanitizeNumber(quantity, 1, 100) || 1,
        selected: Boolean(newItem.selected),
        id: key,
        // Sanitize other fields if they exist
        title: sanitizeString(newItem.title, 200),
        price: sanitizeNumber(newItem.price, 0, 1000000),
        image: sanitizeString(newItem.image, 500)
      } as Item;
      
      // Removed verbose logging - cart operations happen too frequently
      await driver.upsert(sanitizedItem);
    } catch (error) {
      // Security: Sanitize error messages
      const sanitizedError = sanitizeError(error);
      // SECURITY: Removed console.warn to prevent information leakage
      toast.error(sanitizedError);
    }
  }, [driver, currentUser?.uid, items]);

  const removeItem = useCallback(async (key: string) => { 
    if (!driver) {
      toast.error('Cart service not available. Please refresh the page.');
      return;
    }
    
    try {
      // Security: Enhanced rate limiting (only for authenticated users)
      if (currentUser?.uid) {
        const rateLimitKey = `remove_item_${currentUser.uid}`;
        const rateLimitResult = cartRateLimiter.isAllowed(rateLimitKey);
        if (!rateLimitResult.allowed) {
          toast.error(rateLimitResult.message || 'Too many cart operations. Please try again later.');
          return;
        }
      }
      
      // Security: Enhanced input validation
      const keyValidation = validateString(key, 'Item key', 1, 200);
      if (!keyValidation.valid) {
        toast.error(keyValidation.message || 'Invalid item key');
        return;
      }
      
      const sanitizedKey = sanitizeString(key, 200);
      if (!sanitizedKey) {
        toast.error('Invalid item key format');
        return;
      }
      
      // Remove the item using the Firestore document ID
      // Note: deleteDoc doesn't throw errors for non-existent documents, so this should always work
      await driver.remove(sanitizedKey);
    } catch (error: any) {
      // Enhanced error logging to help debug issues
      // SECURITY: Removed console.error to prevent information leakage
      
      // Show more specific error messages based on error type
      let errorMessage = 'Failed to remove item from cart';
      
      if (error?.code === 'permission-denied') {
        errorMessage = 'You do not have permission to remove this item. Please try logging out and back in.';
      } else if (error?.code === 'not-found') {
        errorMessage = 'Item not found in cart. It may have already been removed.';
      } else if (error?.code === 'unavailable') {
        errorMessage = 'Service temporarily unavailable. Please try again in a moment.';
      } else if (error?.message) {
        // Show the actual error message if available
        errorMessage = error.message;
      } else {
        // Fallback to sanitized error
        errorMessage = sanitizeError(error);
      }
      
      toast.error(errorMessage);
    }
  }, [driver, currentUser?.uid]);
  
  const setQuantity = useCallback(async (key: string, quantity: number) => { 
    if (!driver) return;
    
    try {
      // Security: Enhanced rate limiting (only for authenticated users)
      if (currentUser?.uid) {
        const rateLimitKey = `set_quantity_${currentUser.uid}`;
        const rateLimitResult = cartRateLimiter.isAllowed(rateLimitKey);
        if (!rateLimitResult.allowed) {
          toast.error(rateLimitResult.message || 'Too many cart operations. Please try again later.');
          return;
        }
      }
      
      // Security: Enhanced input validation
      const keyValidation = validateString(key, 'Item key', 1, 200);
      if (!keyValidation.valid) {
        toast.error(keyValidation.message || 'Invalid item key');
        return;
      }

      const quantityValidation = validateNumber(quantity, 'Quantity', 1, 100);
      if (!quantityValidation.valid) {
        toast.error(quantityValidation.message || 'Invalid quantity');
        return;
      }
      
      const sanitizedKey = sanitizeString(key, 200);
      const sanitizedQuantity = sanitizeNumber(quantity, 1, 100);
      
      if (!sanitizedKey || !sanitizedQuantity) {
        toast.error('Invalid quantity data format');
        return;
      }
      
      // Removed verbose logging - cart operations happen too frequently
      await driver.setQuantity(sanitizedKey, sanitizedQuantity);
    } catch (error) {
      // Security: Sanitize error messages
      const sanitizedError = sanitizeError(error);
      // SECURITY: Removed console.warn to prevent information leakage
      toast.error(sanitizedError);
    }
  }, [driver, currentUser?.uid, toast]);
  
  // Security: Enhanced fast quantity update with input validation and rate limiting
  const fastSetQuantity = useCallback(async (key: string, quantity: number) => {
    if (!currentUser?.uid) return;
    
    try {
      // Security: Enhanced rate limiting
      const rateLimitKey = `fast_set_quantity_${currentUser.uid}`;
      const rateLimitResult = cartRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        toast.error(rateLimitResult.message || 'Too many cart operations. Please try again later.');
        return;
      }
      
      // Security: Enhanced input validation
      const keyValidation = validateString(key, 'Item key', 1, 200);
      if (!keyValidation.valid) {
        toast.error(keyValidation.message || 'Invalid item key');
        return;
      }

      const quantityValidation = validateNumber(quantity, 'Quantity', 1, 100);
      if (!quantityValidation.valid) {
        toast.error(quantityValidation.message || 'Invalid quantity');
        return;
      }
      
      const sanitizedKey = sanitizeString(key, 200);
      const sanitizedQuantity = sanitizeNumber(quantity, 1, 100);
      
      if (!sanitizedKey || !sanitizedQuantity) {
        toast.error('Invalid quantity data format');
        return;
      }
      
      // Find the cart item to get product info
      const item = items.find(i => (i.id || cartKey({ productId: i.productId, variationId: i.variationId })) === key);
      if (!item) {
        toast.error('Item not found in cart');
        return;
      }
      
      // Security: Enhanced product ID validation
      if (!isValidDocumentId(item.productId)) {
        toast.error('Invalid product data');
        return;
      }
      
      // Get cached stock or use a default high value for instant updates
      const availableStock = productStockCache.get(item.productId) || 999;
      
      // Security: Validate stock value
      if (typeof availableStock !== 'number' || availableStock < 0) {
        toast.error('Invalid stock data');
        return;
      }
      
      // Local update function with enhanced security
      const onLocalUpdate = (itemId: string, newQuantity: number) => {
        // Security: Validate update parameters
        if (!itemId || typeof newQuantity !== 'number' || newQuantity < 0) {
          // SECURITY: Removed console.warn to prevent information leakage
          return;
        }
        
        setItems(prev => prev.map(i => {
          const itemKey = i.id || cartKey({ productId: i.productId, variationId: i.variationId });
          return itemKey === itemId ? { ...i, quantity: Math.max(0, Math.min(100, newQuantity)) } : i;
        }));
      };
      
      // Security: Enhanced error handler with sanitized messages
      const onError = (error: string) => {
        const sanitizedError = sanitizeError(error);
        // SECURITY: Removed console.warn to prevent information leakage
        toast.error(sanitizedError);
      };
      
      // Removed verbose logging - cart operations happen too frequently
      await fastUpdateCartQuantity(
        currentUser.uid,
        sanitizedKey,
        sanitizedQuantity,
        availableStock,
        onLocalUpdate,
        onError
      );
    } catch (error) {
      // Security: Sanitize error messages
      const sanitizedError = sanitizeError(error);
      // SECURITY: Removed console.warn to prevent information leakage
      toast.error(sanitizedError);
    }
  }, [currentUser?.uid, items, productStockCache]);
  
  const toggleSelected = useCallback(async (key: string, selected: boolean) => { 
    if (!driver || !currentUser?.uid) return;
    
    try {
      // Security: Enhanced rate limiting
      const rateLimitKey = `toggle_selected_${currentUser.uid}`;
      const rateLimitResult = cartRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        toast.error(rateLimitResult.message || 'Too many cart operations. Please try again later.');
        return;
      }
      
      // Security: Enhanced input validation
      const keyValidation = validateString(key, 'Item key', 1, 200);
      if (!keyValidation.valid) {
        toast.error(keyValidation.message || 'Invalid item key');
        return;
      }

      const selectedValidation = validateBoolean(selected, 'Selected state');
      if (!selectedValidation.valid) {
        toast.error(selectedValidation.message || 'Invalid selected state');
        return;
      }
      
      const sanitizedKey = sanitizeString(key, 200);
      if (!sanitizedKey) {
        toast.error('Invalid item key format');
        return;
      }
      
      // Removed verbose logging - cart operations happen too frequently
      await driver.toggleSelected(sanitizedKey, Boolean(selected));
    } catch (error) {
      // Security: Sanitize error messages
      const sanitizedError = sanitizeError(error);
      // SECURITY: Removed console.warn to prevent information leakage
      toast.error(sanitizedError);
    }
  }, [driver, currentUser?.uid, toast]);
  
  const clearCart = useCallback(async () => { 
    if (!driver || !currentUser?.uid) return;
    
    try {
      // Security: Enhanced rate limiting
      const rateLimitKey = `clear_cart_${currentUser.uid}`;
      const rateLimitResult = cartRateLimiter.isAllowed(rateLimitKey);
      if (!rateLimitResult.allowed) {
        toast.error(rateLimitResult.message || 'Too many cart operations. Please try again later.');
        return;
      }
      
      // Removed verbose logging - cart operations happen too frequently
      await driver.clear();
    } catch (error) {
      // Security: Sanitize error messages
      const sanitizedError = sanitizeError(error);
      // SECURITY: Removed console.warn to prevent information leakage
      toast.error(sanitizedError);
    }
  }, [driver, currentUser?.uid, toast]);

  // Backward-compat method names
  const addToCart = useCallback(async (item: Omit<Item, 'quantity'>, quantity?: number) => addOrUpdateItem(item, quantity), [addOrUpdateItem]);
  const removeFromCart = useCallback(async (key: string) => removeItem(key), [removeItem]);
  const updateQuantity = useCallback(async (key: string, quantity: number) => setQuantity(key, quantity), [setQuantity]);

  // Performance: Memoize expensive calculations
  const totalItems = useMemo(() => 
    items.reduce((sum, item) => sum + (item.quantity || 0), 0), 
    [items]
  );
  
  const totalPrice = useMemo(() => 
    items.reduce((sum, item) => sum + ((item.price || 0) * (item.quantity || 0)), 0), 
    [items]
  );

  // Performance: Memoize context value to prevent unnecessary re-renders
  const value: CartContextType = useMemo(() => ({
    items,
    loading,
    totalItems,
    totalPrice,
    addOrUpdateItem,
    removeItem,
    setQuantity,
    fastSetQuantity,
    toggleSelected,
    clearCart,
    cacheProductStock,
    // compat
    addToCart,
    removeFromCart,
    updateQuantity,
  }), [
    items,
    loading,
    totalItems,
    totalPrice,
    addOrUpdateItem,
    removeItem,
    setQuantity,
    fastSetQuantity,
    toggleSelected,
    clearCart,
    cacheProductStock,
    addToCart,
    removeFromCart,
    updateQuantity,
  ]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 